import { inject, Injectable } from '@angular/core';
import { SignatureModalComponent } from '@shared/modals/signature-modal/signature-modal.component';
import { ModalController } from '@ionic/angular/modal-controller';
import { AdditionalCustomerInfoModalComponent } from '@shared/modals/additional-customer-info-modal/additional-customer-info-modal.component';
import { DocumentRequestModalComponent } from '@shared/modals/document-request-modal/document-request-modal.component';
import { AnnualFiguresModalComponent } from '@shared/modals/annual-figures-modal/annual-figures-modal.component';
import { NotificationDto } from '@bpp-stella/api/stella';
import { NotificationDataDto } from '@bpp-stella/api/push';
import { TaskNotificationModalDataModel } from '@shared/models/notification/task-notification-modal-data.model';

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private readonly modalController = inject(ModalController);

  public async openSignatureModalAsync(notification: NotificationDto): Promise<void> {
    await this.openTaskModalAsync(
      SignatureModalComponent,
      this.mapStellaNotificationToTaskModalData(notification)
    );
  }

  public async openSignatureModalFromNotificationAsync(notification: NotificationDataDto): Promise<void> {
    await this.openTaskModalAsync(
      SignatureModalComponent,
      this.mapPushNotificationToTaskModalData(notification)
    );
  }

  public async openAdditionalCustomerInfoModalAsync(notification: NotificationDto): Promise<void> {
    await this.openTaskModalAsync(
      AdditionalCustomerInfoModalComponent,
      this.mapStellaNotificationToTaskModalData(notification)
    );
  }

  public async openAdditionalCustomerInfoModalFromNotificationAsync(notification: NotificationDataDto): Promise<void> {
    await this.openTaskModalAsync(
      AdditionalCustomerInfoModalComponent,
      this.mapPushNotificationToTaskModalData(notification)
    );
  }

  public async openDocumentRequestModalAsync(notification: NotificationDto): Promise<void> {
    await this.openTaskModalAsync(
      DocumentRequestModalComponent,
      this.mapStellaNotificationToTaskModalData(notification)
    );
  }

  public async openDocumentRequestModalFromNotificationAsync(notification: NotificationDataDto): Promise<void> {
    await this.openTaskModalAsync(
      DocumentRequestModalComponent,
      this.mapPushNotificationToTaskModalData(notification)
    );
  }

  public async openAnnualFiguresModalAsync(notification: NotificationDto): Promise<void> {
    await this.openTaskModalAsync(
      AnnualFiguresModalComponent,
      this.mapStellaNotificationToTaskModalData(notification)
    );
  }

  public async openAnnualFiguresModalFromNotificationAsync(notification: NotificationDataDto): Promise<void> {
    await this.openTaskModalAsync(
      AnnualFiguresModalComponent,
      this.mapPushNotificationToTaskModalData(notification)
    );
  }

  private async openTaskModalAsync(
    component: typeof SignatureModalComponent | typeof AdditionalCustomerInfoModalComponent | typeof DocumentRequestModalComponent | typeof AnnualFiguresModalComponent,
    notification: TaskNotificationModalDataModel
  ): Promise<void> {
    await this.dismissTaskModalAsync();

    const modal = await this.modalController.create({
      component: component,
      componentProps: {
        notification: notification
      },
      // Both properties are required: Ionic derives `isSheetModal` from breakpoints
      // AND initialBreakpoint, and without the array it keeps the height but drops the
      // grab handle, the rounded top corners and the drag. The leading 0 is what makes
      // swipe-to-dismiss work.
      breakpoints: [0, 0.75, 1],
      initialBreakpoint: 0.75
    });

    await modal.present();
  }

  private async dismissTaskModalAsync(): Promise<void> {
    const topModal = await this.modalController.getTop();

    if (!topModal) {
      return;
    }

    await topModal.dismiss();
  }

  private mapStellaNotificationToTaskModalData(notification: NotificationDto): TaskNotificationModalDataModel {
    return {
      id: notification.id ?? null,
      brokernetTrackingEvent: notification.brokernetTrackingEvent ?? null,
      notificationUrl: notification.notificationUrl ?? null,
      anmerkung: notification.anmerkung ?? null,
      notificationObjectKey: notification.notificationObjectKey ?? null,
      notificationObjectName: notification.notificationObjectName ?? null
    };
  }

  // anmerkung is the one type source the push payload does not carry.
  private mapPushNotificationToTaskModalData(notification: NotificationDataDto): TaskNotificationModalDataModel {
    return {
      id: notification.id ?? null,
      brokernetTrackingEvent: notification.brokernetTrackingEvent ?? null,
      notificationUrl: notification.url ?? null,
      anmerkung: null,
      notificationObjectKey: notification.notificationObjectKey ?? null,
      notificationObjectName: notification.notificationObjectName ?? null
    };
  }

  public closeModal(): void {
    void this.modalController.dismiss();
  }
}
